import { Box, Typography, Button, Chip, Divider, Paper, Stack } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GroupIcon from '@mui/icons-material/Group';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import VideocamIcon from '@mui/icons-material/Videocam';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

const EMBED_CALENDAR_RE = /calendar\.google\.com\/calendar\/embed/i;
const EMBED_SLIDES_RE   = /docs\.google\.com\/presentation\/.+?(\/embed|\/pub\?|[?&]embedded=true)/i;
const EMBED_DRIVE_RE    = /drive\.google\.com\/file\/.+\/preview/i;

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

/** Only rendered when URL is confirmed embeddable via regex. */
const EmbedFrame = ({ url, title, height = 420 }) => (
  <Box sx={{ width: '100%', height, overflow: 'hidden', flexShrink: 0 }}>
    <iframe
      src={url}
      title={title}
      width="100%"
      height="100%"
      style={{ border: 'none', display: 'block' }}
      allowFullScreen
    />
  </Box>
);

/** Placeholder shown when a URL is configured but not embeddable. */
const EmbedPlaceholder = ({ hint }) => (
  <Box sx={{ px: 2.5, py: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      Embedding unavailable for this resource. Use the Open button to launch in a new tab.
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.disabled">
        {hint}
      </Typography>
    )}
  </Box>
);

const EMBED_HINTS = {
  calendar: 'To enable inline preview: Google Calendar → Settings → Get embeddable link (use the /calendar/embed URL)',
  slides:   'To enable inline preview: Google Slides → File → Share → Publish to web → Embed tab (copy the /embed URL)',
  drive:    'To enable inline preview: use a direct file preview link in the format drive.google.com/file/d/{id}/preview',
};

/**
 * Right-column operational panel — shows embed if URL pattern qualifies,
 * otherwise shows a clean placeholder. Always shows an Open button in the header.
 */
const EmbedPanel = ({ title, url, isEmbeddable, launchLabel, LaunchIcon, hintKey, embedHeight = 420 }) => {
  if (!url) return null;
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1.5 }}>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <LaunchIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="text"
          endIcon={<OpenInNewIcon fontSize="inherit" />}
          onClick={() => openExternal(url)}
          sx={{ textTransform: 'none', fontSize: '0.75rem', flexShrink: 0 }}
        >
          {launchLabel}
        </Button>
      </Box>

      {isEmbeddable
        ? <EmbedFrame url={url} title={title} height={embedHeight} />
        : <EmbedPlaceholder hint={EMBED_HINTS[hintKey]} />}
    </Paper>
  );
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}
  >
    {children}
  </Typography>
);

const CollaborationWorkspaceContent = ({ workspace, workspaceName, onDetach }) => {
  if (!workspace) return null;

  const {
    title,
    description,
    participants = [],
    googleMeetUrl,
    zoomMeetingUrl,
    googleCalendarUrl,
    googleSlidesUrl,
    googleDriveUrl,
    notes,
  } = workspace;

  const displayTitle    = title || workspace?.name || workspaceName || 'Collaboration Workspace';
  const calendarIsEmbed = !!googleCalendarUrl && EMBED_CALENDAR_RE.test(googleCalendarUrl);
  const slidesIsEmbed   = !!googleSlidesUrl   && EMBED_SLIDES_RE.test(googleSlidesUrl);
  const driveIsEmbed    = !!googleDriveUrl     && EMBED_DRIVE_RE.test(googleDriveUrl);

  /** Meet + Zoom — primary contained buttons (external only, never embed) */
  const primaryActions = [
    googleMeetUrl  && { key: 'meet', label: 'Join Google Meet', Icon: VideoCallIcon, url: googleMeetUrl,  variant: 'contained' },
    zoomMeetingUrl && { key: 'zoom', label: 'Join Zoom',        Icon: VideocamIcon,  url: zoomMeetingUrl, variant: 'contained' },
  ].filter(Boolean);

  /** Calendar / Slides / Drive — secondary outlined buttons in the left sidebar */
  const secondaryActions = [
    googleCalendarUrl && { key: 'calendar', label: 'Open Calendar', Icon: CalendarMonthIcon, url: googleCalendarUrl },
    googleSlidesUrl   && { key: 'slides',   label: 'Open Slides',   Icon: SlideshowIcon,     url: googleSlidesUrl   },
    googleDriveUrl    && { key: 'drive',    label: 'Open Drive',     Icon: FolderOpenIcon,    url: googleDriveUrl    },
  ].filter(Boolean);

  const allLaunchActions = [...primaryActions, ...secondaryActions];
  const hasRightColumn   = googleCalendarUrl || googleSlidesUrl || googleDriveUrl;
  const hasContent       = allLaunchActions.length > 0 || (Array.isArray(participants) && participants.length > 0) || !!notes;

  return (
    <Box>
      {/* ── Top header ── */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25 }}>
              {displayTitle}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
          {onDetach && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={onDetach}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Open in New Window
            </Button>
          )}
        </Stack>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {!hasContent && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No collaboration links have been configured for this workspace yet.
        </Typography>
      )}

      {/* ── 2-column operational layout ── */}
      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* ── LEFT COLUMN — controls + info ── */}
        <Box
          sx={{
            flex: { xs: '1 1 auto', md: '0 0 270px' },
            width: { xs: '100%', md: 270 },
            minWidth: 0,
          }}
        >
          {/* Quick Launch */}
          {allLaunchActions.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <SectionLabel>Quick Launch</SectionLabel>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                <Stack spacing={1}>
                  {primaryActions.map(({ key, label, Icon, url, variant }) => (
                    <Button
                      key={key}
                      variant={variant}
                      size="small"
                      fullWidth
                      startIcon={<Icon fontSize="small" />}
                      endIcon={<OpenInNewIcon fontSize="inherit" />}
                      onClick={() => openExternal(url)}
                      sx={{ textTransform: 'none', fontWeight: 500, justifyContent: 'flex-start' }}
                    >
                      {label}
                    </Button>
                  ))}
                  {secondaryActions.map(({ key, label, Icon, url }) => (
                    <Button
                      key={key}
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<Icon fontSize="small" />}
                      endIcon={<OpenInNewIcon fontSize="inherit" />}
                      onClick={() => openExternal(url)}
                      sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
              </Paper>
            </Box>
          )}

          {/* Participants */}
          {Array.isArray(participants) && participants.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <SectionLabel>Participants</SectionLabel>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {participants.map((p, i) => (
                  <Chip key={i} icon={<GroupIcon />} label={p} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}

          {/* Notes */}
          {notes && (
            <Box sx={{ mb: 2.5 }}>
              <SectionLabel>Notes</SectionLabel>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <NoteAltIcon fontSize="small" color="action" sx={{ mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {notes}
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          )}
        </Box>

        {/* ── RIGHT COLUMN — embedded operational panels ── */}
        {hasRightColumn && (
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <EmbedPanel
              title="Shared Calendar"
              url={googleCalendarUrl}
              isEmbeddable={calendarIsEmbed}
              launchLabel="Open"
              LaunchIcon={CalendarMonthIcon}
              hintKey="calendar"
              embedHeight={420}
            />
            <EmbedPanel
              title="Shared Slides"
              url={googleSlidesUrl}
              isEmbeddable={slidesIsEmbed}
              launchLabel="Open"
              LaunchIcon={SlideshowIcon}
              hintKey="slides"
              embedHeight={420}
            />
            <EmbedPanel
              title="Shared Documents"
              url={googleDriveUrl}
              isEmbeddable={driveIsEmbed}
              launchLabel="Open"
              LaunchIcon={FolderOpenIcon}
              hintKey="drive"
              embedHeight={420}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CollaborationWorkspaceContent;
